// ── Auth ──────────────────────────────────────────────────────────────────

export interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export type AuthResponse = AuthUser & AuthTokens;

export interface RegisterRequest {
  email: string;
  password: string;
  displayName: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RefreshRequest {
  refreshToken: string;
}

export type RefreshResponse = AuthTokens;

export interface LogoutRequest {
  refreshToken?: string;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
  };
}

// ── Openings ──────────────────────────────────────────────────────────────

/** The color a repertoire line is trained from. */
export type Side = 'white' | 'black';

/** 1 = club beginner, 5 = advanced theory. */
export type Difficulty = 1 | 2 | 3 | 4 | 5;

/**
 * How often you meet an opening at club level, 1 (rare) to 5 (constantly).
 *
 * Derived from the real club-game counts in `OpeningStats` by
 * `scripts/fetch_popularity.py`, with fixed thresholds rather than catalog
 * percentiles so adding an opening never silently re-rates the others. The
 * meter is for scanning a list at a glance; `stats` carries the numbers.
 */
export type Popularity = 1 | 2 | 3 | 4 | 5;

/**
 * Real game statistics for an opening's position, taken from the Lichess games
 * database filtered to club ratings — not the masters database, because what
 * grandmasters play is a different question from what you will face at 1800.
 *
 * `games` and `sharePercent` answer different questions and genuinely disagree
 * about which opening is "more popular", so both are kept rather than picking
 * one and calling it the truth:
 *
 * - `games` — how many club games reach this position, i.e. how often you will
 *   meet it. Affected by how deep the opening is defined here: a five-ply
 *   headline will always count fewer games than a two-ply one.
 * - `sharePercent` — how many games choose this opening at the moment the
 *   choice arises, i.e. whether it is the main line there. The Yugoslav Attack
 *   takes most of its branch, but few games ever reach that branch.
 */
export interface OpeningStats {
  /** Where the numbers came from, e.g. "lichess". */
  source: string;
  /** Rating band they were filtered to, e.g. "1600-2200". */
  ratings: string;
  speeds: string;
  /** ISO date of the fetch, so stale figures are visible rather than implied. */
  fetchedAt: string;
  games: number;
  sharePercent: number;
  /** Points per game for White, as a percentage. */
  whiteScore: number;
}

/**
 * How far into an opening a line sits. A repertoire entry carries a level and
 * only schedules lines at or below it, so raising your level is what "go
 * deeper into this opening" means. Ordered shallowest to deepest.
 *
 * `opening` is the opening's own moves and nothing more. It is less a
 * difficulty than a place to put the trunk, so the sequence every variation is
 * built on does not sit in the same bucket as the variations themselves. Not
 * every opening has one: where the trunk is a single move there is nothing to
 * drill, so those ladders simply start at `beginner`.
 */
export type LineLevel = 'opening' | 'beginner' | 'intermediate' | 'advanced' | 'master';

/**
 * Curriculum tiers — a different axis from line levels, and deliberately
 * without `opening`: a whole opening is never "the opening level".
 */
export type Tier = Exclude<LineLevel, 'opening'>;

export const TIERS: Tier[] = ['beginner', 'intermediate', 'advanced', 'master'];

export const LINE_LEVELS: LineLevel[] = ['opening', 'beginner', 'intermediate', 'advanced', 'master'];

/**
 * Top-level grouping used by the browse filters. Mirrors how opening books are
 * organized rather than strict ECO ranges, because it is what you actually
 * think in when choosing what to learn.
 */
export type OpeningFamily =
  | 'Open Games'        // 1.e4 e5
  | 'Semi-Open Games'   // 1.e4 anything else
  | 'Closed Games'      // 1.d4 d5
  | 'Indian Defenses'   // 1.d4 Nf6
  | 'Flank Openings'    // 1.c4, 1.Nf3, 1.b3 …
  | 'Gambits';

/**
 * A single move in a repertoire line.
 *
 * `why` is the whole point of the app: on every move the trainee has to find,
 * it says what the move accomplishes, not just that it is "the book move".
 */
export interface LineMove {
  /** Standard algebraic notation, e.g. "Nf3", "exd5", "O-O", "Qxd8+". */
  san: string;
  /** Why this move is played. Present on every move the trainee must find. */
  why?: string;
  /** Marks a move that is the point of the whole line — surfaced in review. */
  critical?: boolean;
  /** Tempting wrong moves here, each played out far enough to show why. */
  mistakes?: MoveMistake[];
}

/**
 * A plausible wrong move, played out far enough to show why it fails.
 *
 * Naming a mistake is not enough to learn from it: "don't play ...Na5 here" is
 * a rule to memorise, while watching the knight get trapped four moves later is
 * a reason you can reconstruct at the board. The refutation is replayed on a
 * real board at build time, because a punishment that does not actually work
 * teaches a false lesson with complete confidence.
 */
export interface MoveMistake {
  san: string;
  /** Why it is tempting — a mistake has to be made attractive before it teaches. */
  why: string;
  refutation: RefutationMove[];
  /** What is concretely worse now: which squares, pieces and plans. */
  consequence: string;
}

export interface RefutationMove {
  san: string;
  note?: string;
}

/**
 * One concrete variation, always given from the initial position so it can be
 * replayed as a self-contained practice. Lines within an opening share a prefix;
 * that repetition is deliberate — repeating the first moves every rep is what
 * builds the memory.
 */
export interface Line {
  id: string;
  openingId: string;
  name: string;
  eco: string;
  /** The side the trainee plays. Moves at plies of the other color auto-play. */
  side: Side;
  summary: string;
  difficulty: Difficulty;
  level: LineLevel;
  moves: LineMove[];
  /**
   * How many opening plies this line shares with the opening's headline.
   *
   * Every line of an opening starts the same way, so the part worth attending
   * to is what comes *after* this point — the UI marks the branch rather than
   * presenting twenty plies as if they were all equally new.
   */
  trunkPlies: number;
  /** True for the line that is the opening's own moves and nothing more. */
  isTrunk?: boolean;
  /** Position after the last move, for the "you have reached this" diagram. */
  finalFen: string;
  /** What has been achieved by the end of the line. */
  finalComment: string;
  /**
   * What to do once the memorised moves run out.
   *
   * A line that ends with "White's pawns are weak" and no way to attack them
   * leaves you exactly where you started — which is the most common complaint
   * about opening trainers, and the reason this is structured rather than prose.
   */
  plan?: LinePlan;
}

export interface LinePlan {
  /** Concrete things to aim for, not adjectives. */
  yours: string[];
  /** What the opponent is trying to do, so it can be recognised and stopped. */
  theirs: string[];
  counters?: PlanCounter[];
}

export interface PlanCounter {
  /** The try you need to recognise. */
  when: string;
  /** What to do about it. */
  answer: string;
}

/**
 * The strategic content for an opening — the "why" behind the moves, kept
 * separate from the moves themselves so it can be read on its own.
 */
export interface OpeningTheory {
  /** The core idea in two or three sentences. */
  idea: string;
  /** The pawn structure that arises and what it dictates. */
  pawnStructure: string;
  /** Where the pieces belong and why. */
  pieces: string;
  /** The middlegame plans each side is aiming for. */
  plans: { white: string[]; black: string[] };
  /** Squares the whole fight revolves around, e.g. ["d5", "f5"]. */
  keySquares: string[];
  /** What people get wrong here, and what it costs them. */
  typicalMistakes: string[];
  /** When this opening suits you, and when it does not. */
  whenToPlay: string;
  /** Openings this can transpose into or out of. */
  transpositions?: string[];
}

/** Lightweight opening shape for the browse/search list. */
export interface OpeningSummary {
  id: string;
  name: string;
  /** ECO code or range, e.g. "C53" or "B90–B99". */
  eco: string;
  family: OpeningFamily;
  side: Side;
  /** The defining move sequence, e.g. "1.e4 e5 2.Nf3 Nc6 3.Bc4". */
  moves: string;
  /** Position after `moves`, used for the browse-card diagram. */
  fen: string;
  summary: string;
  difficulty: Difficulty;
  /**
   * Which openings to learn next, as opposed to how deep to go inside one of
   * them (that is a line's `level`). The two are independent: the Italian is a
   * beginner-tier opening that still has master-level lines, so a beginner can
   * work through the beginner tier *or* go all the way down one opening.
   *
   * Unlike `popularity` this is a teaching judgment, not a measurement.
   */
  tier: Tier;
  popularity: Popularity;
  /** Absent only for an opening authored since the last statistics fetch. */
  stats?: OpeningStats;
  tags: string[];
  lineCount: number;
  /** Total moves the trainee has to know across all lines. */
  moveCount: number;
}

/** Full opening detail returned by GET /openings/{id}. */
export interface Opening extends OpeningSummary {
  theory: OpeningTheory;
  lines: Line[];
}

/** Paginated response from GET /openings. */
export interface OpeningsResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  openings: OpeningSummary[];
}

/** Response from GET /openings/filters. */
export interface OpeningFilters {
  families: OpeningFamily[];
  sides: Side[];
  difficulties: Difficulty[];
  /** Curriculum tiers present in the catalog, shallowest first. */
  tiers: Tier[];
  tags: string[];
}

export type OpeningSort = 'name' | 'difficulty' | 'eco' | 'lines' | 'popularity';

export interface OpeningSearchParams {
  search?: string;
  families?: string[];
  sides?: string[];
  difficulties?: number[];
  /** Curriculum tiers, e.g. ['beginner'] to work through the starter course. */
  tiers?: string[];
  tags?: string[];
  page?: number;
  pageSize?: number;
  sort?: OpeningSort;
}

// ── Repertoire ────────────────────────────────────────────────────────────

/**
 * An opening the user has taken on. Adding one makes its lines eligible for
 * the study queue; removing one stops scheduling them but keeps the history.
 */
/**
 * A suggested route through the catalog, so "what should I learn next" is never
 * a decision you have to make.
 *
 * Derived from the catalog rather than authored separately, so it cannot drift
 * when openings are added or re-tiered. Ordered by tier, and *within* a tier it
 * rotates between what you play as White and your answers to 1.e4 and 1.d4 —
 * otherwise the path would hand you four White openings and no defenses, which
 * is not a repertoire you could take into a game.
 */
export interface Pathway {
  /** The whole route, in order, numbered from 1. */
  steps: PathStep[];
  /** The one thing to do next, or null when the path is finished. */
  nextUp: PathStep | null;
  doneCount: number;
  totalCount: number;
  /** The White first move this path is built around, e.g. "e4". */
  firstMove: string;
  /** One entry per situation the repertoire has to cover. */
  slots: PathSlot[];
}

/**
 * One situation a repertoire has to cover — "as White against 1.e4 c5" — and
 * what currently fills it.
 */
export interface PathSlot {
  key: string;
  label: string;
  /** Share of your games you reach this situation in, 0-100. */
  reachPercent: number;
  openingId: string | null;
  openingName: string | null;
  level: LineLevel | null;
  /** True when your own repertoire fills it, rather than the path's suggestion. */
  yours: boolean;
}

/**
 * Whether an opening's next level is worth unlocking yet, and if not, why.
 *
 * Computed server-side and sent with both the pathway and the study queue, so
 * the same button is never enabled on one screen and greyed on another.
 */
export interface DepthAdvice {
  currentLevel: LineLevel;
  /** The next level that actually has lines; null at the top of the ladder. */
  nextLevel: LineLevel | null;
  /** How many lines raising to `nextLevel` would add. */
  nextLevelLines: number;
  canDeepen: boolean;
  /** What to do first, when `canDeepen` is false and there is a next level. */
  blockedReason: string | null;
  /** True when per-line choices exist that raising the level would discard. */
  resetsCustomSelection: boolean;
}

export type PathStatus = 'done' | 'current' | 'later';

/**
 * A step is an (opening, level) pair, not just an opening.
 *
 * That is the whole point of the design: the path has to be able to say "take
 * the Italian to intermediate" as well as "learn the Scandinavian", because
 * for someone who plays 1.e4 the first of those is often worth more.
 */
export interface PathStep {
  /** 1-based, so the UI can say "Step 7" without counting. */
  number: number;
  openingId: string;
  name: string;
  side: Side;
  eco: string;
  moves: string;
  family: OpeningFamily;
  fen: string;
  summary: string;
  /** Every line the opening has. */
  lineCount: number;
  tier: Tier;
  popularity: Popularity;
  difficulty: Difficulty;
  /** The situation this opening covers. */
  slot: string;
  slotLabel: string;
  /** "learn" a new opening, or "deepen" one you already play. */
  kind: 'learn' | 'deepen';
  /** The rung this step is about. */
  level: LineLevel;
  /** Lines this step adds to your schedule. */
  linesAdded: number;
  /** Share of your games this material comes up in, 0-100. */
  reachPercent: number;
  status: PathStatus;
  mastery: number;
  scheduledLines: number;
  /** Present only on the rung your repertoire is currently sitting at. */
  depth: DepthAdvice | null;
}

export interface RepertoireEntry {
  openingId: string;
  name: string;
  side: Side;
  eco: string;
  /** Enough of the opening to render the same card the browse page shows. */
  family: OpeningFamily;
  moves: string;
  fen: string;
  summary: string;
  difficulty: Difficulty;
  tier: Tier;
  popularity: Popularity;
  addedAt: number;
  /** How deep the user is working in this opening. */
  level: LineLevel;
  /** The lines actually scheduled, after any per-line choices. */
  lineCount: number;
  /** Every line the opening has, so the UI can say "8 of 14". */
  totalLineCount: number;
  /** Lines never reviewed. */
  newCount: number;
  /** Lines whose next review is in the past. */
  dueCount: number;
  /** 0-100, averaged over the opening's lines from their review intervals. */
  mastery: number;
  /**
   * Exactly which lines are scheduled, resolved server-side from the level and
   * any per-line overrides. Returned so the UI can tick the right boxes without
   * reimplementing the rule and drifting out of step with it.
   */
  selectedLineIds: string[];
  /** True when the selection is not simply "everything up to `level`". */
  customized: boolean;
  /**
   * Lines available at each level, and how many are currently scheduled.
   *
   * Sent with every entry so a level control can be rendered anywhere the
   * repertoire appears without fetching the whole opening first. Levels the
   * opening has no lines at are omitted rather than sent as zero, so a UI can
   * distinguish "none exist" from "none selected".
   */
  levelCounts: Partial<Record<LineLevel, number>>;
  selectedByLevel: Partial<Record<LineLevel, number>>;
  /** True when raising the level would bring more lines into scope. */
  hasDeeperLines: boolean;
}

export interface AddRepertoireRequest {
  openingId: string;
  /** Where to start. Omitted means `beginner`. */
  level?: LineLevel;
}

/** Body for PUT /repertoire/{openingId}. */
export interface SetRepertoireLevelRequest {
  level: LineLevel;
}

// ── Study (spaced repetition) ─────────────────────────────────────────────

/** How well a line went. Maps onto the SM-2 quality scale in the backend. */
export type ReviewGrade = 'again' | 'hard' | 'good' | 'easy';

export type LineState = 'new' | 'learning' | 'review';

/** Per-user, per-line scheduling state. */
export interface LineProgress {
  lineId: string;
  openingId: string;
  state: LineState;
  /** Successful repetitions in a row; reset to 0 by a lapse. */
  reps: number;
  /** How many times a known line was forgotten. */
  lapses: number;
  /** SM-2 ease factor, 1.3 – 2.8. */
  ease: number;
  intervalDays: number;
  dueAt: number;
  lastReviewedAt: number | null;
  totalReviews: number;
  /** Rolling share of moves played correctly first time, 0-100. */
  accuracy: number;
}

export type QueueReason = 'due' | 'new' | 'extra';

/** One item to study: the line itself plus the context needed to render it. */
export interface QueueItem {
  line: Line;
  opening: {
    id: string;
    name: string;
    side: Side;
    eco: string;
    family: OpeningFamily;
  };
  progress: LineProgress | null;
  reason: QueueReason;
}

/** Response from GET /study/queue. */
export interface StudyQueue {
  items: QueueItem[];
  /** Totals across the whole repertoire, not just the returned page. */
  dueCount: number;
  newCount: number;
  /**
   * New lines in this session sitting beyond the suggested daily pace.
   *
   * They are included, not withheld. Metering new material has a real purpose —
   * everything learned today comes back tomorrow, then in three days, so a big
   * intake now becomes a wall of reviews later — but that is a reason to
   * mention it, not to refuse someone a variation they are curious about.
   * Always 0 in study mode and when a session was narrowed to one line.
   */
  newBeyondPace: number;
  /** The suggested pace itself, so the UI can name the number. */
  newPerDay: number;
  /**
   * Lines the repertoire level is keeping out of this session. Not withheld —
   * `scope=all` returns them — but the UI cannot say "7 of 31" unless the
   * server says what 31 is.
   */
  beyondLevel: number;
  /** Every line of every opening in the repertoire, whatever its level. */
  totalInRepertoire: number;
  /**
   * Per opening in this session: whether its next level is worth unlocking.
   * Keyed by opening id, so the study page can offer the level control without
   * a second request and without reimplementing the rule.
   */
  depth: Record<string, DepthAdvice>;
}

/** Body for POST /study/review. */
export interface ReviewRequest {
  lineId: string;
  grade: ReviewGrade;
  /** Moves played correctly without a hint or retry. */
  movesCorrect: number;
  movesTotal: number;
}

export interface ReviewResponse {
  progress: LineProgress;
  stats: StudyStats;
}

/**
 * Response from GET /study/home — the whole home screen in one request.
 *
 * `/study/stats` and `/repertoire` still exist and return exactly these two
 * halves, but the home page needs both and each endpoint independently reloads
 * the same user state, so asking for them separately cost two round trips and
 * two duplicate pairs of DynamoDB queries.
 */
export interface StudyHome {
  stats: StudyStats;
  repertoire: RepertoireEntry[];
}

/** Response from GET /study/stats. */
export interface StudyStats {
  /** Consecutive days with at least one review, ending today or yesterday. */
  currentStreak: number;
  longestStreak: number;
  /** ISO date (YYYY-MM-DD) of the last day with a review. */
  lastStudyDate: string | null;
  totalReviews: number;
  reviewsToday: number;
  /** Lines with at least one successful review. */
  linesLearned: number;
  linesInRepertoire: number;
  dueNow: number;
  newAvailable: number;
}

// ── Settings ──────────────────────────────────────────────────────────────

export type BoardTheme = 'walnut' | 'slate' | 'green' | 'ice';

export interface UserSettings {
  /** Which side's openings to surface first when browsing. */
  preferredSide: Side;
  /** Cap on brand-new lines introduced per day. */
  newPerDay: number;
  /** Cap on total reviews per session. */
  reviewLimit: number;
  boardTheme: BoardTheme;
  showCoordinates: boolean;
  /** Milliseconds to pause before the opponent replies, so moves register. */
  opponentDelayMs: number;
}

export type UpdateSettingsRequest = Partial<UserSettings>;

// ── Coach (LLM explanations — phase 2) ────────────────────────────────────

/**
 * Ask for an explanation of an arbitrary position rather than a pre-authored
 * one. Disabled by default; the endpoint answers `{ enabled: false }` unless
 * COACH_ENABLED is set on the API Lambda. See backend-rust/api/src/handlers/coach.rs.
 */
export interface CoachRequest {
  /** Position to explain. */
  fen: string;
  /** SAN moves that led to it, for context. */
  moves: string[];
  /** Free-form question; defaults to "why is this move played?". */
  question?: string;
  openingId?: string;
  lineId?: string;
}

export interface CoachResponse {
  enabled: boolean;
  answer: string | null;
  /** Set when `enabled` is false, explaining how to turn it on. */
  reason?: string;
}
